import { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AppContext } from "@/AppContext";
import { getBasicUserInfo } from "@/api/auth";
import { canAccess, docsUrl, Feature, settingsPath } from "@/access/userTypes";

/**
 * The one avatar menu for every user (replaces the platform AvatarDropdown and the
 * Agents UserMenu while the unified shell switch is on). Short on purpose: Settings (which holds
 * Profile, Team, Clients, Domains, Integrations, Connect Claude), Documentation, Support, Sign Out.
 * What shows is decided by canAccess() in access/userTypes.ts.
 */
type Item = { feature: Feature; label: string; to?: string; href?: string };

const SUPPORT_URL = "https://support.launchos.com/hc/launch-os-help-center/en/categories/launch-os";

const cls = "block w-full px-3 py-2 text-left text-[13px] text-gray-700 hover:bg-gray-50";

export function UnifiedUserMenu() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { permissions = [] } = useContext(AppContext);
  const user = getBasicUserInfo() || {};
  const who = { email: user.email, permissions };

  const name = [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email || "Your account";
  const initials = (name.match(/\b\w/g) || ["?"]).slice(0, 2).join("").toUpperCase();
  // Everything that is a Settings tab lives in Settings; the menu only carries the way in, help, and sign out.
  const ITEMS: Item[] = [
    { feature: "settings", label: "Settings", to: settingsPath(who) },
    { feature: "docs", label: "Documentation", href: docsUrl(who) },
    { feature: "support", label: "Support", href: SUPPORT_URL },
  ];
  const items = ITEMS.filter((i) => canAccess(who, i.feature));

  const signOut = () => {
    setOpen(false);
    navigate("/login?signout=1");
  };

  return (
    <div className="relative flex justify-center pt-5 pb-3" data-testid="unified-user-menu">
      <button
        data-testid="unified-user-menu-button"
        onClick={() => setOpen(!open)}
        title={name}
        className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-500 text-[10px] font-bold text-white ring-2 ring-white shadow-sm transition-all duration-150 hover:shadow-md hover:ring-gray-100"
      >
        {initials}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-[calc(100%+12px)] top-0 z-50 w-56 overflow-hidden rounded-xl border border-black/[0.06] bg-white shadow-xl">
            <div className="border-b border-black/[0.06] px-3 py-3">
              <p className="truncate text-[13px] font-semibold text-gray-900">{name}</p>
              <p className="truncate text-[11px] text-gray-400">{user.email}</p>
            </div>
            <div className="py-1">
              {items.map((i) =>
                i.to ? (
                  <Link key={i.label} to={i.to} onClick={() => setOpen(false)} className={cls}>
                    {i.label}
                  </Link>
                ) : (
                  <a key={i.label} href={i.href} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)} className={cls}>
                    {i.label}
                  </a>
                )
              )}
            </div>
            <div className="border-t border-black/[0.06] py-1">
              <button onClick={signOut} className={cls}>
                Sign Out
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
